import React from 'react'
import { motion } from 'framer-motion'
import { Price } from 'lucide-react'
import { Check, Star, Download, Monitor } from 'lucide-react'
import melhoradorLogo from '../../../../assets/images/Melhorador-logo.png'
import { PricingGate } from '../../../../components/payment/PricingGate'

interface PricingProps {
  onSubscribe?: () => void;
  onLoginRequest?: () => void;
}

export default function Pricing({ onSubscribe, onLoginRequest }: PricingProps) {
  return (
    <section id="pricing" className="py-24 relative flex items-center justify-center overflow-hidden bg-black">

      {/* Glow Effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-cyber-blue/10 blur-[150px] rounded-full pointer-events-none"></div>

      <div className="container mx-auto px-6 relative z-10">

        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-black font-display text-white mb-4">
            LICENÇA <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue to-purple-500">PRO</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-lg mx-auto">
            Acesso completo ao software mais poderoso de upscale do mercado.
          </p>
        </div>

        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          whileHover={{ y: -5 }}
          viewport={{ once: true }}
          className="max-w-md mx-auto relative group"
        >
          {/* Animated Border */}
          <div className="absolute -inset-[2px] bg-gradient-to-r from-cyber-blue via-purple-500 to-cyber-blue rounded-[32px] opacity-75 group-hover:opacity-100 blur-sm transition duration-500 animate-pulse-slow"></div>

          <div className="relative bg-gray-900 rounded-[30px] overflow-hidden p-8 md:p-10 border border-white/10">

            <div className="absolute top-0 right-0 bg-cyber-blue/20 text-cyber-blue px-4 py-1 rounded-bl-2xl font-bold text-xs uppercase tracking-widest border-b border-l border-cyber-blue/30 z-20">
              Mais Vendido
            </div>

            <PricingGate
              variant="dark"
              onLoginRequest={onLoginRequest || (() => { })}
              productName="Melhorador Desktop"
            >
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-gray-800 to-black rounded-2xl flex items-center justify-center mb-6 border border-white/10 shadow-lg p-3">
                  <img
                    src={melhoradorLogo}
                    alt="Melhorador Desktop Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Desktop Pro</h3>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-gray-500 line-through text-lg">R$ 599</span>
                  <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs font-bold">ECONOMIZE R$ 100</span>
                </div>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-gray-400 text-xl font-light">R$</span>
                  <span className="text-6xl font-black text-white tracking-tighter">499</span>
                  <span className="text-gray-400 text-lg font-light">/ano</span>
                </div>
              </div>

              <ul className="space-y-4 mb-10">
                <PricingItem text="Instalação em 1 PC (GPU)" />
                <PricingItem text="Infinitas Artes (+10.000/mês)" />
                <PricingItem text="Upscale Extremo até 12X" />
                <PricingItem text="+Modelos de IA Exclusivos" />
                <PricingItem text="Todas as atualizações incluídas" />
                <PricingItem text="Suporte Prioritário" />
              </ul>

              <button
                onClick={onSubscribe}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-cyber-blue to-purple-600 text-white font-black text-lg shadow-[0_0_20px_rgba(0,243,255,0.3)] relative overflow-hidden group hover:shadow-[0_0_40px_rgba(0,243,255,0.5)] transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  COMPRAR AGORA <Star className="w-4 h-4 fill-white" />
                </span>
                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 skew-x-12"></div>
              </button>

              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500">
                <Download className="w-3 h-3" />
                <span>Download imediato após pagamento</span>
              </div>
            </PricingGate>
          </div>
        </motion.div>

      </div>
    </section>
  )
}

const PricingItem = ({ text }: { text: string }) => (
  <li className="flex items-start gap-3 text-gray-300">
    <div className="mt-1 min-w-[1.25rem] h-5 rounded-full bg-cyber-blue/10 flex items-center justify-center">
      <Check className="w-3 h-3 text-cyber-blue" />
    </div>
    <span className="text-sm">{text}</span>
  </li>
)
